import React, { useEffect, useState } from 'react';
import CustomContent from '../../../components/Content/CustomContent';
import { Button, Card, Col, Divider, Modal, Popconfirm, Row, Select, Space, Table, Typography } from 'antd';
import { deleteData, fetchData, saveData } from '../../service/dataServices';
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined, RightOutlined } from '@ant-design/icons';
import TranslatableInput from '../../components/form/TranslatableInput';
import { getLocalizedTitle } from '../../utils/localization';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { Text } = Typography;

const Dropdowns = () => {
  const { t } = useTranslation();
  const [dropdowns, setDropdowns] = useState({});
  const [dropdownTypes, setDropdownTypes] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [selectedDropdownType, setSelectedDropdownType] = useState();
  const [modalVisible, setModalVisible] = useState(false);
  const [inputValue, setInputValue] = useState({ en: '', ar: '' });
  const [modalOperationType, setModalOperationType] = useState('Add');
  const [selectedDropdown, setSelectedDropdown] = useState();
  const [childDropdownType, setChildDropdownType] = useState(null);
  const [childTableData, setChildTableData] = useState([]);
  const [parentDetails, setParentDetails] = useState(null);
  const [isChildAdd, setIsChildAdd] = useState(false);


  const dropdownsResponse = fetchData('/api/dropdowns', response => setDropdowns(response));
  const typesResponse = fetchData('/api/dropdowns/types', response => setDropdownTypes(response));
  const saveMutation = saveData('/api/dropdowns', 'POST');
  const updateMutation = saveData('/api/dropdowns', 'PUT');
  const deleteMutation = deleteData('/api/dropdowns');

  useEffect(() => {
    if (selectedDropdownType) {
      setTableData(dropdowns[selectedDropdownType]);
    }
  }, [dropdowns]);

  const onSelect = (val) => {
    if (val && val !== '') {
      const found = dropdownTypes.find(d => d.title === val);
      if (found && found.child) {
        setChildDropdownType(found.child);
      } else {
        setChildDropdownType(null);
      }
      setTableData(dropdowns[val]);
      setSelectedDropdownType(val);
    } else {
      setTableData([]);
      setSelectedDropdownType(null);
    }
  };

  const onDelete = (id) => {
    deleteMutation.mutate({ idList: id });
    if (parentDetails && parentDetails.id === id) {
      setParentDetails(null);
      setChildTableData([])
    }
  };

  const getTranslationsFromOption = (dropdown) => {
    if (dropdown.translations) {
      return { en: dropdown.translations.en || '', ar: dropdown.translations.ar || '' };
    }
    return { en: dropdown.title || '', ar: '' };
  };

  const onUpdate = (dropdown) => {
    setIsChildAdd(false);
    setSelectedDropdown(dropdown);
    setModalOperationType('Update');
    setInputValue(getTranslationsFromOption(dropdown));
    setModalVisible(true);
  };

  const onUpdateChild = dropdown => {
    setIsChildAdd(true);
    setSelectedDropdown(dropdown);
    setModalOperationType('Update');
    setInputValue(getTranslationsFromOption(dropdown));
    setModalVisible(true);
  }

  const onAddNew = () => {
    setIsChildAdd(false);
    setSelectedDropdown(null);
    setModalOperationType('Add');
    setInputValue({ en: '', ar: '' });
    setModalVisible(true);
  };

  // same
  const onAddNewChild = () => {
    setIsChildAdd(true);
    setSelectedDropdown(null);
    setModalOperationType('Add');
    setInputValue({ en: '', ar: '' });
    setModalVisible(true);
  };

  const onModalOk = () => {
    setModalVisible(false);
    if (inputValue.en && inputValue.en.length > 0) {
      const translations = {
        en: inputValue.en,
        ar: inputValue.ar || '',
      };
      // Remove empty translations
      Object.keys(translations).forEach(k => {
        if (!translations[k]) delete translations[k];
      });

      let dataToSend = {
        'title': inputValue.en,
        'translations': translations,
        'dropdownType': selectedDropdownType,
      };
      if (isChildAdd) {
        dataToSend.parentId = parentDetails.id;
        dataToSend.dropdownType = childDropdownType.title;
      }
      if (modalOperationType === 'Add') {
        saveMutation.mutate({ dataToSend });
      } else if (modalOperationType === 'Update') {
        updateMutation.mutate({ 'dataToSend': dataToSend, 'id': selectedDropdown.id });
      } else {
      }
    }
  };

  const onShowChildren = (dd) => {
    setChildTableData(dd.children);
    setParentDetails(dd);
  }

  useEffect(() => {
    if (dropdowns && parentDetails && selectedDropdownType) {
      const found = dropdowns[selectedDropdownType].find(dd => dd.id === parentDetails.id);
      if (found && found.children) {
        setChildTableData(found.children);
      } else {
        setChildTableData([]);
      }

    }
  }, [dropdowns])

  const convertCamelCaseToSentence = (text) => {
    if (text) {
      const result = text.replace(/([A-Z])/g, ' $1');
      return result.charAt(0).toUpperCase() + result.slice(1);
    }
    return "";
  };

  const childColumns = [
    {
      title: t('common.title'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => getLocalizedTitle(a).localeCompare(getLocalizedTitle(b)),
      render: (text, record) => getLocalizedTitle(record),
    },
    {
      title: t('common.update'),
      key: 'update',
      render: (val) => {
        return (<Button onClick={() => onUpdateChild(val)} shape="circle" icon={<EditOutlined />} />);
      },
    },
    {
      title: t('common.delete'),
      key: 'delete',
      render: (val) => {
        return (
          <Popconfirm
            title={t('common.confirmation')}
            description={t('common.areYouSureToDelete')}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => onDelete(val.id)}
            okButtonProps={{ size: 'middle' }}
            okText={t('common.yes')}
            cancelButtonProps={{ size: 'middle' }}
            styles={{ body: { padding: 16 } }}
          >
            <Button shape="circle" icon={<DeleteOutlined />} />
          </Popconfirm>);
      },
    },
  ]

  let parentColumns = [
    {
      title: t('common.title'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => getLocalizedTitle(a).localeCompare(getLocalizedTitle(b)),
      render: (text, record) => getLocalizedTitle(record),
    },
    {
      title: t('common.update'),
      key: 'update',
      render: (val) => {
        return (<Button onClick={() => onUpdate(val)} shape="circle" icon={<EditOutlined />} />);
      },
    },
    {
      title: t('common.delete'),
      key: 'delete',
      render: (val) => {
        return (
          <Popconfirm
            title={t('common.confirmation')}
            description={t('common.areYouSureToDelete')}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => onDelete(val.id)}
            okButtonProps={{ size: 'middle' }}
            okText={t('common.yes')}
            cancelButtonProps={{ size: 'middle' }}
            styles={{ body: { padding: 16 } }}
          >
            <Button shape="circle" icon={<DeleteOutlined />} />
          </Popconfirm>);
      },
    },
  ];

  if (childDropdownType) {
    parentColumns = [
      ...parentColumns,
      {
        title: t('dropdowns.child'),
        key: 'child',
        render: (val) => {
          return (<Button onClick={() => onShowChildren(val)} shape="circle" icon={<RightOutlined />} />);
        },
      },
    ]
  }

  return (
    (<CustomContent>
      <Row>
        <Col span={12}>
          <Space direction="horizontal" style={{ width: '100%' }} wrap>
            <Select
              showSearch
              filterOption
              optionFilterProp="children"
              placeholder={t('dropdowns.selectDropdownType')}
              value={selectedDropdownType}
              allowClear
              style={{ width: '25vh' }}
              onSelect={onSelect}
              onClear={() => {
                setTableData([]);
                setSelectedDropdownType(null);
                setChildTableData([]);
                setChildDropdownType(null);
              }}
            >
              {
                dropdownTypes && dropdownTypes.map(option => (
                  <Option
                    value={option.title}
                    key={option.title}
                  >
                    {convertCamelCaseToSentence(option.title)}
                  </Option>
                ))
              }
            </Select>
          </Space>
        </Col>
      </Row>
      <Divider />
      <Row gutter={16} align="middle" justify="center">
        <Col span={12}>
          <Card
            title={convertCamelCaseToSentence(selectedDropdownType)}
            extra={(<Button
              disabled={!selectedDropdownType}
              onClick={onAddNew}
              icon={<PlusOutlined />}>
              {t('common.addNew')}
            </Button>)}
          >
            <Table
              loading={
                dropdownsResponse.isFetching
                || saveMutation.isLoading
                || updateMutation.isLoading
                || deleteMutation.isLoading
              }
              dataSource={tableData}
              columns={parentColumns}
              rowKey={record => record.id}
              pagination={false}
              expandable={{ childrenColumnName: 'not-expandable' }}
            />
          </Card>
        </Col>
        {childDropdownType && (
          <Col span={12}>
            <Card
              title={convertCamelCaseToSentence(childDropdownType.title)}
              extra={(<Button
                disabled={parentDetails === null}
                onClick={onAddNewChild}
                icon={<PlusOutlined />}>
                {t('common.addNew')}
              </Button>)}
            >
              <Table
                loading={
                  dropdownsResponse.isFetching
                  || saveMutation.isLoading
                  || updateMutation.isLoading
                  || deleteMutation.isLoading
                }
                dataSource={childTableData}
                columns={childColumns}
                rowKey={record => record.id}
                pagination={false}
                expandable={{ childrenColumnName: 'not-expandable' }}
              />
            </Card>
          </Col>
        )}
      </Row>
      <Modal
        title={modalOperationType === 'Add' ? t('dropdowns.addDropdown') : t('dropdowns.updateDropdown')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={onModalOk}
        closable={false}
        destroyOnHidden
        centered
        width={700}
      >
        <Space direction="vertical" align="stretch" style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <TranslatableInput
              value={inputValue}
              onChange={setInputValue}
              placeholder={{ en: t('dropdowns.dropdownValueEn'), ar: t('dropdowns.dropdownValueAr') }}
            />
          </div>
          <Text type="secondary">
            {isChildAdd && parentDetails && (<div>{t('dropdowns.parent')}: {getLocalizedTitle(parentDetails)}</div>)}
          </Text>

        </Space>
      </Modal>
    </CustomContent>)
  );
};

export default Dropdowns;